import axios from 'axios'
import type { ApiResponse } from '@/types/api'

export interface UploadResponse {
  url: string
  objectName: string
  contentType: string
  size: number
}

export interface UploadProgress {
  loaded: number
  total: number
  percentage: number
}

let _uploadClient: ReturnType<typeof axios.create> | null = null

const getUploadClient = () => {
  if (!_uploadClient) {
    _uploadClient = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1',
      timeout: 600000,
    })
    _uploadClient.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken')
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })
  }
  return _uploadClient
}

export const uploadService = {
  uploadVideo: async (
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<ApiResponse<UploadResponse>> => {
    return uploadFile(file, '/uploads/video', onProgress)
  },

  uploadDocument: async (
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<ApiResponse<UploadResponse>> => {
    return uploadFile(file, '/uploads/document', onProgress)
  },

  uploadImage: async (
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<ApiResponse<UploadResponse>> => {
    return uploadFile(file, '/uploads/image', onProgress)
  },

  deleteFile: async (objectName: string): Promise<ApiResponse<void>> => {
    const client = getUploadClient()
    const response = await client.delete(`/uploads/${encodeURIComponent(objectName)}`)
    const body = response.data
    return {
      success: body.success,
      data: body.data,
      error: body.error,
    }
  },
}

async function uploadFile(
  file: File,
  endpoint: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<ApiResponse<UploadResponse>> {
  const formData = new FormData()
  formData.append('file', file)

  const client = getUploadClient()

  const response = await client.post(endpoint, formData, {
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress({
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          percentage,
        })
      }
    },
  })

  const body = response.data

  return {
    success: body.success,
    data: body.data ? {
      url: body.data.url,
      objectName: body.data.object_name || body.data.objectName,
      contentType: body.data.content_type || body.data.contentType,
      size: body.data.size,
    } : undefined,
    error: body.error,
  }
}

// File type validation helpers
export const isValidVideoFile = (file: File): boolean => {
  const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska']
  const allowedExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv']

  const extension = '.' + file.name.split('.').pop()?.toLowerCase()
  return allowedTypes.includes(file.type) || allowedExtensions.includes(extension)
}

export const isValidDocumentFile = (file: File): boolean => {
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
  ]
  const allowedExtensions = ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx', '.txt']

  const extension = '.' + file.name.split('.').pop()?.toLowerCase()
  return allowedTypes.includes(file.type) || allowedExtensions.includes(extension)
}

export const isValidImageFile = (file: File): boolean => {
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
  const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp']

  const extension = '.' + file.name.split('.').pop()?.toLowerCase()
  return allowedTypes.includes(file.type) || allowedExtensions.includes(extension)
}

// Size limits
export const MAX_VIDEO_SIZE = 500 * 1024 * 1024 // 500MB
export const MAX_DOCUMENT_SIZE = 50 * 1024 * 1024 // 50MB
export const MAX_IMAGE_SIZE = 10 * 1024 * 1024 // 10MB

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export default uploadService
